import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../../../config/api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  History as HistoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Contacts as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useSnackbar } from 'notistack';
import {
  AdminQuoteWithItems,
  AdminQuoteItem,
  QuotePriceChangeLog,
  getAdminQuoteWithItems,
  updateAdminQuoteItemPrice,
  getQuotePriceChangeLogs,
  adminUpdateQuoteObservation,
  adminUpdateQuoteFields,
  syncQuoteCreditAnalysis,
} from '../../../services/admin-service';
import { Contact } from '../../../types';
import * as quoteService from '../../../services/quote-service';
import CreditAnalysisConfirmDialog from '../../../components/CreditAnalysisConfirmDialog/CreditAnalysisConfirmDialog';
import { formatPhone } from 'utils/formatters';

interface Product {
  id: string;
  name: string;
  sku: string;
  description: string;
  price: number;
  images?: Array<{
    id: string;
    image_url: string;
    alt_text: string;
    is_primary: boolean;
  }>;
}

const AdminQuoteDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [quote, setQuote] = useState<AdminQuoteWithItems | null>(null);
  const [observation, setObservation] = useState<string>('');
  const [savingObservation, setSavingObservation] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  // State for editing unit prices
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editedUnitPrice, setEditedUnitPrice] = useState<number | null>(null);
  const [editedQuantity, setEditedQuantity] = useState<number | null>(null);
  const [savingPrice, setSavingPrice] = useState(false);

  // State to track recently updated prices for visual feedback
  const [recentlyUpdatedItems, setRecentlyUpdatedItems] = useState<string[]>([]);

  const [tabValue, setTabValue] = useState(0);
  const [priceLogs, setPriceLogs] = useState<QuotePriceChangeLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [logsPage, setLogsPage] = useState<number>(1);
  const [logsTotal, setLogsTotal] = useState<number>(0);
  const [converting, setConverting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [openAddProductModal, setOpenAddProductModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [addingProduct, setAddingProduct] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  // Edit payment dialog state
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState<string>('');
  const [newPaymentTerm, setNewPaymentTerm] = useState<string>('30');
  const [savingPayment, setSavingPayment] = useState(false);
  const [santanderDialogOpen, setSantanderDialogOpen] = useState(false);
  const [santanderProposalInput, setSantanderProposalInput] = useState('');
  const [savingSantanderProposal, setSavingSantanderProposal] = useState(false);

  const paymentStartIcon = savingPayment ? (
    <CircularProgress size={18} color="inherit" />
  ) : (
    <SaveIcon />
  );

  const handleOpenSantanderDialog = () => {
    setSantanderProposalInput('');
    setSantanderDialogOpen(true);
  };

  const handleCloseSantanderDialog = () => {
    setSantanderDialogOpen(false);
    setSantanderProposalInput('');
  };

  const handleSaveSantanderProposal = async () => {
    if (!id) return;
    const trimmedProposal = santanderProposalInput.trim();
    if (!trimmedProposal) {
      enqueueSnackbar('Informe o número da proposta Santander', { variant: 'warning' });
      return;
    }

    setSavingSantanderProposal(true);
    try {
      const response = await syncQuoteCreditAnalysis(id, {
        santander_proposal_number: trimmedProposal,
      });

      if (response?.status) {
        enqueueSnackbar(response.message || 'Proposta sincronizada com sucesso', {
          variant: 'success',
        });
        setQuote(prev =>
          prev
            ? {
                ...prev,
                santander_proposal_number: response.santander_proposal_number,
                is_syncing_with_santander: true,
              }
            : prev
        );
        setSantanderDialogOpen(false);
        setSantanderProposalInput('');
      } else {
        enqueueSnackbar(response?.message || 'Erro ao sincronizar proposta', { variant: 'error' });
        setQuote(prev =>
          prev
            ? {
                ...prev,
                is_syncing_with_santander: false,
              }
            : prev
        );
      }
    } catch (error) {
      enqueueSnackbar('Erro ao sincronizar proposta Santander', { variant: 'error' });
    } finally {
      setSavingSantanderProposal(false);
    }
  };

  const handleCopySantanderUrl = async () => {
    if (!quote?.santander_proposal_number) return;
    const url = `https://acer-santander.cisp1.io/${quote.santander_proposal_number}/finish-formalizations`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      enqueueSnackbar('Url copiado para area de trasnferencia.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Não foi possível copiar a URL.', { variant: 'error' });
    }
  };

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Cotações', path: '/admin/quotes' },
      { label: `Cotação ${quote?.code || id}` },
    ]);
  }, [setBreadcrumbs, id, quote]);

  // Clear recently updated items indicator after 3 seconds
  useEffect(() => {
    if (recentlyUpdatedItems.length > 0) {
      const timer = setTimeout(() => {
        setRecentlyUpdatedItems([]);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [recentlyUpdatedItems]);

  useEffect(() => {
    const loadQuoteDetails = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const quoteData = await getAdminQuoteWithItems(id);

        if (quoteData) {
          setQuote(quoteData);
          setObservation((quoteData as any).observation || '');
        } else {
          enqueueSnackbar('Cotação não encontrada', { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('Erro ao carregar detalhes da cotação', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadQuoteDetails();
  }, [id, enqueueSnackbar]);

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusChip = (status: string) => {
    const statusMap: Record<
      string,
      {
        color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
        label: string;
      }
    > = {
      Draft: { color: 'default', label: 'Rascunho' },
      Submitted: { color: 'info', label: 'Enviada' },
      Pending: { color: 'warning', label: 'Pendente' },
      Aprovado: { color: 'success', label: 'Aprovada' },
      Rejected: { color: 'error', label: 'Rejeitada' },
      Converted: { color: 'primary', label: 'Convertida' },
    };

    const statusInfo = statusMap[status] || { color: 'default', label: status };
    return (
      <Chip label={statusInfo.label} color={statusInfo.color} size="small" variant="outlined" />
    );
  };

  const handleEditUnitPrice = (item: AdminQuoteItem) => {
    setEditingItemId(item.id);
    setEditedUnitPrice(item.unit_price);
    setEditedQuantity(item.quantity);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditedUnitPrice(null);
    setEditedQuantity(null);
  };

  const handleSaveUnitPrice = async (itemId: string) => {
    if (editedUnitPrice === null || editedQuantity === null || !quote) return;

    setSavingPrice(true);
    try {
      // Find the original item to compare prices later
      const originalItem = (quote.quote_items || []).find(item => item.id === itemId);
      const originalPrice = originalItem ? originalItem.unit_price : 0;
      const originalQuantity = originalItem ? originalItem.quantity : 0;

      const updatedItem = await updateAdminQuoteItemPrice(itemId, editedUnitPrice, editedQuantity);
      if (updatedItem) {
        // Update the local state with the updated item
        const updatedItems = (quote.quote_items || []).map(item =>
          item.id === itemId
            ? {
                ...item,
                unit_price: editedUnitPrice,
                quantity: editedQuantity,
              }
            : item
        );
        setQuote({ ...quote, quote_items: updatedItems });

        // Add to recently updated items for visual feedback
        setRecentlyUpdatedItems(prev => [...prev, itemId]);

        // Show appropriate feedback based on the changes
        let message = '';
        if (editedUnitPrice !== originalPrice) {
          message += `Preço ${editedUnitPrice > originalPrice ? 'aumentado' : 'reduzido'} de ${formatCurrency(originalPrice)} para ${formatCurrency(editedUnitPrice)}. `;
        }
        if (editedQuantity !== originalQuantity) {
          message += `Quantidade alterada de ${originalQuantity} para ${editedQuantity}.`;
        }
        if (message) {
          enqueueSnackbar(message, { variant: 'success' });
        } else {
          enqueueSnackbar('Nenhuma alteração realizada', { variant: 'info' });
        }
      } else {
        enqueueSnackbar('Falha ao atualizar item', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error updating item:', error);
      enqueueSnackbar('Erro ao atualizar item', { variant: 'error' });
    } finally {
      setSavingPrice(false);
      setEditingItemId(null);
      setEditedUnitPrice(null);
      setEditedQuantity(null);
    }
  };

  const handleUnitPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setEditedUnitPrice(value);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setEditedQuantity(value);
    }
  };

  // Function to fetch price change logs
  const fetchPriceChangeLogs = async () => {
    if (!id) return;

    setLoadingLogs(true);
    try {
      // Get the auth token
      const token = localStorage.getItem('token');

      // Make a direct axios call to the backend
      const response = await axios.get(
        `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${id}/price-change-logs?skip=${(logsPage - 1) * 10}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.items) {
        setPriceLogs(response.data.items);
        setLogsTotal(response.data.total || 0);
      } else {
        console.warn('No price logs received or items array is empty');
        setPriceLogs([]);
        setLogsTotal(0);
      }
    } catch (error: any) {
      console.error('Error fetching price change logs:', error);
      enqueueSnackbar(`Falha ao carregar histórico: ${error.message || 'Erro desconhecido'}`, {
        variant: 'error',
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  // Load logs when tab changes to logs tab or when the component mounts
  useEffect(() => {
    if (tabValue === 1) {
      fetchPriceChangeLogs();
    }
  }, [tabValue, id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Função para abrir o dialog de confirmação de análise de crédito
  const handleConvertToOrder = async () => {
    setCreditDialogOpen(true);
  };

  // Função para processar a conversão após confirmação
  const handleCreditAnalysisConfirm = async (verifyCredit: boolean) => {
    if (!id) return;

    try {
      setConverting(true);
      setCreditDialogOpen(false);

      // Mostrar mensagem de processamento
      enqueueSnackbar('Processando conversão...', { variant: 'info' });

      // Chamar o serviço para converter a cotação em pedido
      const response = await quoteService.convertQuoteToOrder(id, verifyCredit);
      if (response.success) {
        // Exibir mensagem de sucesso
        enqueueSnackbar('Cotação convertida em pedido com sucesso!', { variant: 'success' });

        // Redirecionar para a página de pedidos
        navigate('/admin/orders');
      } else {
        const msg = response.message || 'Erro ao converter cotação em pedido';
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao converter cotação:', error);
      const serverMsg = error?.response?.data?.detail || error?.message;
      enqueueSnackbar(serverMsg || 'Erro ao converter cotação em pedido', { variant: 'error' });
    } finally {
      setConverting(false);
    }
  };

  // Função para fechar o dialog de confirmação
  const handleCreditDialogClose = () => {
    setCreditDialogOpen(false);
  };

  const handleDeleteQuote = async () => {
    if (!id) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');

      await axios.delete(`${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      enqueueSnackbar('Cotação excluída com sucesso.', { variant: 'success' });
      navigate('/admin/quotes');
    } catch (error) {
      console.error('Erro ao excluir cotação:', error);
      enqueueSnackbar('Erro ao excluir cotação', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenAddProductModal = async () => {
    setOpenAddProductModal(true);
    await fetchAvailableProducts();
  };

  const handleCloseAddProductModal = () => {
    setOpenAddProductModal(false);
  };

  const handleOpenContactsModal = () => {
    setOpenContactsModal(true);
  };

  const handleCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const fetchAvailableProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.BASE_URL_WITH_VERSION}/admin/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data && response.data.items) {
        setAvailableProducts(response.data.items);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar('Erro ao carregar produtos', { variant: 'error' });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleAddProductToQuote = async (productId: string) => {
    if (!id || !quote) return;

    setAddingProduct(productId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${id}/items`,
        {
          product_id: productId,
          quantity: 1,
          unit_price: availableProducts.find(p => p.id === productId)?.price || 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        // Refresh quote data
        const updatedQuote = await getAdminQuoteWithItems(id);
        if (updatedQuote) {
          setQuote(updatedQuote);
          enqueueSnackbar('Produto adicionado com sucesso', { variant: 'success' });
        }
      }
    } catch (error) {
      console.error('Error adding product to quote:', error);
      enqueueSnackbar('Erro ao adicionar produto', { variant: 'error' });
    } finally {
      setAddingProduct(null);
    }
  };

  const handleDeleteQuoteItem = async (itemId: string) => {
    if (!id || !quote) return;

    setDeletingItemId(itemId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_CONFIG.BASE_URL_WITH_VERSION}/admin/quotes/${id}/items/${itemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Refresh quote data
      const updatedQuote = await getAdminQuoteWithItems(id);
      if (updatedQuote) {
        setQuote(updatedQuote);
        enqueueSnackbar('Item removido com sucesso', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error deleting quote item:', error);
      enqueueSnackbar('Erro ao remover item', { variant: 'error' });
    } finally {
      setDeletingItemId(null);
    }
  };

  // Render price change logs table
  const renderPriceLogs = () => {
    console.log('Rendering price logs. Current state:', {
      loadingLogs,
      priceLogs: priceLogs?.length || 0,
      tabValue,
    });

    if (loadingLogs) {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (!priceLogs || priceLogs.length === 0) {
      console.log('No price logs to display');
      return (
        <Box textAlign="center" my={4}>
          <Typography variant="body1" color="textSecondary">
            Nenhum histórico de alteração de preço encontrado para esta cotação.
          </Typography>
        </Box>
      );
    }

    console.log('Rendering price logs table with data:', priceLogs);

    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Administrador</TableCell>
              <TableCell>Quantidade Anterior</TableCell>
              <TableCell>Nova Quantidade</TableCell>
              <TableCell>Preço Anterior</TableCell>
              <TableCell>Novo Preço</TableCell>
              <TableCell>Alteração</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceLogs.map(log => {
              const priceDifference = log.new_price - log.previous_price;
              const percentChange = (priceDifference / log.previous_price) * 100;

              return (
                <TableRow key={log.id}>
                  <TableCell>{formatDateTime(log.created_at)}</TableCell>
                  <TableCell>{log.admin?.name || 'Admin desconhecido'}</TableCell>
                  <TableCell>{log.previous_quantity || 'N/A'}</TableCell>
                  <TableCell>{log.new_quantity || 'N/A'}</TableCell>
                  <TableCell>{formatCurrency(log.previous_price)}</TableCell>
                  <TableCell>{formatCurrency(log.new_price)}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        color:
                          priceDifference > 0
                            ? 'success.main'
                            : priceDifference < 0
                              ? 'error.main'
                              : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {priceDifference > 0 ? '+' : ''}
                      {formatCurrency(priceDifference)}
                      <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                        ({priceDifference > 0 ? '+' : ''}
                        {percentChange.toFixed(2)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box p={3}>
        <Typography variant="h5" color="error">
          Cotação não encontrada
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/quotes')}
          sx={{ mt: 2 }}
        >
          Voltar para Cotações
        </Button>
      </Box>
    );
  }

  const calculateTotalAmount = () => {
    return (quote.quote_items || []).reduce((sum, item) => {
      const itemTotal = item.unit_price * item.quantity;
      return sum + itemTotal;
    }, 0);
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/admin/quotes')} edge="start" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Detalhes da Cotação: {quote.code || 'Rascunho'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Primeira linha */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '250px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Informações do Cliente</Typography>
                <Button
                  color="primary"
                  startIcon={<ContactsIcon />}
                  onClick={handleOpenContactsModal}
                  size="small"
                >
                  Ver Contatos
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" mb={1}>
                <BusinessIcon sx={{ mr: 1 }} />
                <Typography variant="body1">{quote.customer?.company_name}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                CNPJ: {quote.customer?.cnpj}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Email: {quote.customer?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Telefone:{' '}
                {quote.customer?.phone ? formatPhone(quote.customer?.phone) : 'Não informado'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '250px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Informações do Revendedor
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" mb={1}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="body1">{quote.reseller?.name}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Email: {quote.reseller?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Telefone:{' '}
                {quote.reseller?.phone ? formatPhone(quote.reseller?.phone) : 'Não informado'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Segunda linha */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '250px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Endereço de Entrega
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="flex-start" mb={1}>
                <LocationOnIcon sx={{ mr: 1, mt: 0.3 }} />
                <Box>
                  <Typography variant="body1">
                    {quote.shipping_address?.street} {quote.shipping_address?.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quote.shipping_address?.city}, {quote.shipping_address?.state},{' '}
                    {quote.shipping_address?.zip_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quote.shipping_address?.country}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '250px' }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Detalhes da Cotação
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" mb={1}>
                <CalendarTodayIcon sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Criada em: {formatDate(quote.created_at)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <CalendarTodayIcon sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Expira em: {formatDate(quote.expiration_date)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoneyIcon sx={{ mr: 1 }} />
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Pagamento: {quote.payment_method}, {quote.payment_term} dias
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      // Only allow Bank Slip per requirement
                      setNewPaymentMethod('Bank Slip');
                      setNewPaymentTerm((quote.payment_term as string) || '30');
                      setEditPaymentOpen(true);
                    }}
                  >
                    Editar
                  </Button>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoneyIcon sx={{ mr: 1 }} />
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Proposta Santander: {quote.santander_proposal_number ?? ''}
                  </Typography>
                  {quote.is_syncing_with_santander ? (
                    <Typography variant="body2" color="text.secondary">
                      <CircularProgress size={18} color="inherit" />
                    </Typography>
                  ) : !quote.santander_proposal_number ? (
                    <Button size="small" variant="outlined" onClick={handleOpenSantanderDialog}>
                      <AddIcon />
                      Adicionar Número de Proposta
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopySantanderUrl}
                    >
                      Copiar Url de Formalização
                    </Button>
                  )}
                </Box>
              </Box>

              {quote.status === 'Em Análise' && (
                <Box display="flex" alignItems="center" mb={1}>
                  <AttachFileIcon sx={{ mr: 1 }} />
                  {(quote as any).file_path ? (
                    <Typography variant="body2" color="text.secondary">
                      Documento:{' '}
                      <a
                        href={(quote as any).file_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Visualizar arquivo
                      </a>
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Documento: Verifique se a revenda enviou os detalhes do pedido por e-mail
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Observações - bloco separado full width */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {/* Edit Payment Method Dialog */}
              <Dialog open={editPaymentOpen} onClose={() => setEditPaymentOpen(false)}>
                <DialogTitle>Editar Forma de Pagamento</DialogTitle>
                <DialogContent sx={{ pt: 2, minWidth: 360 }}>
                  <FormControl fullWidth>
                    <InputLabel id="payment-method-label">Forma de Pagamento</InputLabel>
                    <Select
                      labelId="payment-method-label"
                      label="Forma de Pagamento"
                      value={newPaymentMethod}
                      onChange={e => setNewPaymentMethod(e.target.value as string)}
                      disabled
                    >
                      <MenuItem value="Bank Slip">Boleto</MenuItem>
                    </Select>
                  </FormControl>
                  <Box mt={2}>
                    <FormControl fullWidth>
                      <InputLabel id="payment-term-label">Prazo</InputLabel>
                      <Select
                        labelId="payment-term-label"
                        label="Prazo"
                        value={newPaymentTerm}
                        onChange={e => setNewPaymentTerm(e.target.value as string)}
                      >
                        <MenuItem value="30">30</MenuItem>
                        <MenuItem value="60">60</MenuItem>
                        <MenuItem value="90">90</MenuItem>
                        <MenuItem value="30/60">30/60</MenuItem>
                        <MenuItem value="30/60/90">30/60/90</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setEditPaymentOpen(false)} startIcon={<CancelIcon />}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={paymentStartIcon}
                    disabled={savingPayment || !newPaymentMethod || !newPaymentTerm || !id}
                    onClick={async () => {
                      if (!id || !newPaymentMethod || !newPaymentTerm) return;
                      setSavingPayment(true);
                      try {
                        const updated = await adminUpdateQuoteFields(id, {
                          payment_method: 'Bank Slip',
                          payment_term: newPaymentTerm,
                        });
                        if (updated) {
                          setQuote(prev =>
                            prev
                              ? {
                                  ...prev,
                                  payment_method: 'Bank Slip',
                                  payment_term: newPaymentTerm,
                                }
                              : prev
                          );
                          enqueueSnackbar('Forma de pagamento atualizada', {
                            variant: 'success',
                          });
                          setEditPaymentOpen(false);
                        }
                      } catch (err) {
                        enqueueSnackbar('Erro ao atualizar forma de pagamento', {
                          variant: 'error',
                        });
                      } finally {
                        setSavingPayment(false);
                      }
                    }}
                  >
                    Salvar
                  </Button>
                </DialogActions>
              </Dialog>
              {/* Santander Proposal Dialog */}
              <Dialog open={santanderDialogOpen} onClose={handleCloseSantanderDialog}>
                <DialogTitle>Adicionar número de proposta</DialogTitle>
                <DialogContent sx={{ pt: 2, minWidth: 360 }}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Número da proposta Santander"
                    value={santanderProposalInput}
                    onChange={e => setSantanderProposalInput(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseSantanderDialog} startIcon={<CancelIcon />}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      savingSantanderProposal ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={savingSantanderProposal || !santanderProposalInput.trim()}
                    onClick={handleSaveSantanderProposal}
                  >
                    Salvar
                  </Button>
                </DialogActions>
              </Dialog>

              <Typography variant="h6" gutterBottom>
                Observação que sairá na Nota Fiscal
              </Typography>
              <TextField
                multiline
                minRows={4}
                fullWidth
                placeholder="Observações da cotação"
                value={observation}
                onChange={e => setObservation(e.target.value)}
              />
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  onClick={async () => {
                    if (!id) return;
                    setSavingObservation(true);
                    try {
                      const updated = await adminUpdateQuoteObservation(id, observation);
                      if (updated) {
                        setQuote(prev => (prev ? { ...prev, observation } : prev));
                        enqueueSnackbar('Observações salvas', { variant: 'success' });
                      }
                    } catch (err) {
                      enqueueSnackbar('Erro ao salvar observações', { variant: 'error' });
                    } finally {
                      setSavingObservation(false);
                    }
                  }}
                  disabled={savingObservation}
                  startIcon={
                    savingObservation ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                >
                  {savingObservation ? 'Salvando...' : 'Salvar'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Itens da Cotação</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddProductModal}
                >
                  Adicionar Produto
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Itens da Cotação" icon={<AttachMoneyIcon />} iconPosition="start" />
                <Tab label="Histórico de Alterações" icon={<HistoryIcon />} iconPosition="start" />
              </Tabs>

              {tabValue === 0 && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell align="center">Quantidade</TableCell>
                        <TableCell align="right">Preço Unitário</TableCell>
                        <TableCell align="right">Preço Final</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(quote.quote_items || []).map(item => (
                        <TableRow
                          key={item.id}
                          sx={{
                            backgroundColor: recentlyUpdatedItems.includes(item.id)
                              ? 'rgba(76, 175, 80, 0.08)'
                              : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2">
                              {item.product?.name || 'Produto Desconhecido'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.product?.sku || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {editingItemId === item.id ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editedQuantity || ''}
                                onChange={handleQuantityChange}
                                inputProps={{ min: 1, step: 1 }}
                                sx={{ width: '80px' }}
                              />
                            ) : (
                              item.quantity
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingItemId === item.id ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editedUnitPrice || ''}
                                onChange={handleUnitPriceChange}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: '120px' }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: recentlyUpdatedItems.includes(item.id)
                                    ? 'success.main'
                                    : 'text.primary',
                                  fontWeight: recentlyUpdatedItems.includes(item.id)
                                    ? 'bold'
                                    : 'normal',
                                  transition: 'color 0.3s ease-in-out',
                                }}
                              >
                                {formatCurrency(item.unit_price)}
                                {recentlyUpdatedItems.includes(item.id) && ' ✓'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.unit_price * item.quantity)}
                          </TableCell>
                          <TableCell align="right">
                            {editingItemId === item.id ? (
                              <>
                                <Tooltip title="Salvar">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleSaveUnitPrice(item.id)}
                                    disabled={savingPrice}
                                  >
                                    {savingPrice ? <CircularProgress size={24} /> : <SaveIcon />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancelar">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleCancelEdit}
                                    disabled={savingPrice}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Editar Item">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditUnitPrice(item)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir Item">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteQuoteItem(item.id)}
                                    disabled={deletingItemId === item.id}
                                  >
                                    {deletingItemId === item.id ? (
                                      <CircularProgress size={24} />
                                    ) : (
                                      <DeleteIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tabValue === 1 && <>{renderPriceLogs()}</>}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            {(quote.status === 'Em Análise' || quote.status === 'Analise de Credito') && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConvertToOrder}
                disabled={converting}
                startIcon={
                  converting ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />
                }
              >
                {converting ? 'Processando...' : 'Transformar em Pedido'}
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteQuote}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {deleting ? 'Excluindo...' : 'Excluir Cotação'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Contacts Modal */}
      <Dialog
        open={openContactsModal}
        onClose={handleCloseContactsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <ContactsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Contatos de {quote.customer?.company_name || 'Cliente'}
              </Typography>
            </Box>
            <IconButton
              aria-label="fechar"
              onClick={handleCloseContactsModal}
              sx={{
                color: theme => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {(quote.customer as any)?.contacts && (quote.customer as any).contacts.length > 0 ? (
            <Grid container spacing={2}>
              {(quote.customer as any).contacts.map((contact: Contact, index: number) => (
                <Grid item xs={12} sm={6} key={contact.id || index}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      border: contact.is_primary ? '2px solid #80c343' : '1px solid #e0e0e0',
                      position: 'relative',
                    }}
                  >
                    {contact.is_primary && (
                      <Chip
                        label="Principal"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: 8,
                          backgroundColor: '#80c343',
                        }}
                      />
                    )}
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon sx={{ mr: 1, color: '#80c343' }} />
                      <Typography variant="h6" fontWeight="medium">
                        {contact.name}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.email}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {contact.phone ? formatPhone(contact.phone) : 'Não informado'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {contact.role}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
              color="text.secondary"
            >
              <ContactsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Nenhum contato encontrado
              </Typography>
              <Typography variant="body2" textAlign="center">
                Este cliente ainda não possui contatos cadastrados.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactsModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog
        open={openAddProductModal}
        onClose={handleCloseAddProductModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Adicionar Produto à Cotação</DialogTitle>
        <DialogContent>
          {loadingProducts ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {availableProducts.map(product => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card>
                    <CardMedia
                      component="img"
                      sx={{ margin: 'auto', width: 'auto', height: '140px' }}
                      image={
                        product.images?.find(img => img.is_primary)?.image_url || '/placeholder.png'
                      }
                      alt={product.name}
                    />
                    <CardContent>
                      <Typography variant="h6" noWrap>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        SKU: {product.sku}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Preço: {formatCurrency(product.price)}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => handleAddProductToQuote(product.id)}
                        disabled={addingProduct === product.id}
                      >
                        {addingProduct === product.id ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Adicionar'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddProductModal}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação para análise de crédito */}
      <CreditAnalysisConfirmDialog
        open={creditDialogOpen}
        onClose={handleCreditDialogClose}
        onConfirm={handleCreditAnalysisConfirm}
        loading={converting}
      />
    </Box>
  );
};

export default AdminQuoteDetailsPage;
